import type { ChartDataset, Chart as ChartInstance, ChartOptions, Plugin } from 'chart.js';

import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import type { DashboardStatistics } from '~/api/statistics/types';
import type { ColorTheme } from '~/api/user/types';

import { useDashboardStatistics } from '~/api/statistics';
import { useUser } from '~/api/user';
import { Card } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { Chart } from '~/pages/Dashboard/Charts/Chart/Chart';
import { chartStyle } from '~/pages/Dashboard/Charts/Charts.css';
import { useColorThemeContext } from '~/providers/ColorThemeProvider/ColorThemeProvider.context';
import {
  blueOrangePalette,
  bordeauxGrayPalette,
  fontFamily,
  greenPinkPalette,
  neutralPalette,
  purpleYellowPalette,
  redLightBluePalette,
} from '~/styles/constants.css';
import { sumItems } from '~/utils/arrays';

import { ChartCta } from './ChartCta';
import { useStackedBarChartOptions } from './hooks';

const ctaAreaPlugin: Plugin = {
  id: 'ctaAreaSync',
  afterInit(chart) {
    document.fonts.ready.then(() => {
      if (!chart.canvas) return;
      chart.update();
    });
  },
  afterRender(chart) {
    const { bottom, left, right, top } = chart.chartArea;
    const wrapper = chart.canvas.parentElement?.parentElement;
    if (wrapper) {
      wrapper.style.setProperty('--chart-area-top', `${top}px`);
      wrapper.style.setProperty('--chart-area-left', `${left}px`);
      wrapper.style.setProperty('--chart-area-right', `${chart.width - right}px`);
      wrapper.style.setProperty('--chart-area-bottom', `${chart.height - bottom}px`);
    }
  },
};

const EMPTY_PIE_DATA = {
  total: { count: 0, totalFcExclVat: 0 },
  approved: { count: 0, totalFcExclVat: 0 },
  expired: { count: 0, totalFcExclVat: 0 },
  paidCurrentQuarter: { count: 0, totalFcExclVat: 0 },
  paidPreviousQuarters: { count: 0, totalFcExclVat: 0 },
};

const brandColorMap: Record<ColorTheme, { primary: string; secondary: string }> = {
  'green-pink': { primary: greenPinkPalette.primary.$800, secondary: greenPinkPalette.secondary.$300 },
  'purple-yellow': { primary: purpleYellowPalette.primary.$700, secondary: purpleYellowPalette.secondary.$200 },
  'bordeaux-gray': { primary: bordeauxGrayPalette.primary.$950, secondary: bordeauxGrayPalette.secondary.$100 },
  'blue-orange': { primary: blueOrangePalette.primary.$800, secondary: blueOrangePalette.secondary.$300 },
  'red-lightblue': { primary: redLightBluePalette.primary.$500, secondary: redLightBluePalette.secondary.$200 },
};

export const Charts = () => {
  const { data: statistics, isError, isPending } = useDashboardStatistics();

  // Loading and error state are also handled in <StatsSummary />,
  // so it would be redundant to show this twice in the UI.
  if (isPending || isError) return null;

  return <InnerCharts statistics={statistics} />;
};

const InnerCharts = ({ statistics }: { statistics: DashboardStatistics }) => {
  const { data: user } = useUser();

  const { colorTheme } = useColorThemeContext();
  const { primary: primaryColor, secondary: secondaryColor } = brandColorMap[colorTheme];
  const grayColor = neutralPalette.gray.$500;
  const lightColor = neutralPalette.gray.$050;

  const { formatCurrency, locale } = useIntl();

  const { t } = useTranslation(['dashboard']);

  // Bar chart
  const [totalInvoicedYear, setTotalInvoicedYear] = useState(() => statistics.lastActiveYear ?? dayjs().year());
  const totalInvoicedDataset = useMemo<ChartDataset<'bar'>[]>(
    () => [
      {
        label: t('dashboard:charts.labels.performance'),
        data: statistics.barChartDataByYear[totalInvoicedYear]?.totalPerformanceExclVat ?? [],
        backgroundColor: primaryColor,
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 4, bottomRight: 4 },
        borderSkipped: false,
        inflateAmount: -1,
      },
      {
        label: t('dashboard:charts.labels.copyright'),
        data: statistics.barChartDataByYear[totalInvoicedYear]?.totalCopyrightExclVat ?? [],
        backgroundColor: secondaryColor,
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 4, bottomRight: 4 },
        borderSkipped: false,
        inflateAmount: -1,
      },
    ],
    [t, statistics.barChartDataByYear, totalInvoicedYear, primaryColor, secondaryColor],
  );
  const barChartLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => formatter.format(dayjs().day(1).month(i).toDate()));
  }, [locale]);

  const barChartYearData = statistics.barChartDataByYear[totalInvoicedYear];
  const stackedBarOptions = useStackedBarChartOptions(
    sumItems([...(barChartYearData?.totalPerformanceExclVat ?? []), ...(barChartYearData?.totalCopyrightExclVat ?? [])]) > 0,
  );

  // Pie chart
  const chartRef = useRef<ChartInstance<'doughnut', number[]>>(null);
  const [overviewYear, setOverviewYear] = useState(() => statistics.lastActiveYear ?? dayjs().year());
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);

  useEffect(() => {
    setSelectedSegment(null);
    chartRef.current?.setActiveElements([]);
    chartRef.current?.update();
  }, [overviewYear]);

  const overviewLabels = useMemo(
    () => [
      t('dashboard:charts.labels.expired'),
      t('dashboard:charts.labels.notExpired'),
      t('dashboard:charts.labels.currentQ', { quarter: dayjs().quarter() }),
      t('dashboard:charts.labels.previousQ'),
    ],
    [t],
  );

  const pieData = statistics.pieChartDataByYear[overviewYear] ?? EMPTY_PIE_DATA;

  const overviewDataset = useMemo<ChartDataset<'doughnut'>[]>(
    () => [
      {
        data: [pieData.expired.count, pieData.approved.count, pieData.paidCurrentQuarter.count, pieData.paidPreviousQuarters.count],
        type: 'doughnut',
        backgroundColor: [lightColor, grayColor, secondaryColor, primaryColor],
        borderRadius: 999,
        cutout: '85%',
        spacing: 8,
        hoverOffset: 24,
        hoverBorderColor: [lightColor, grayColor, secondaryColor, primaryColor],
        hoverBackgroundColor: [lightColor, grayColor, secondaryColor, primaryColor],
        hoverBorderWidth: 2,
        borderWidth: 0,
        borderColor: [lightColor, grayColor, secondaryColor, primaryColor],
      },
    ],
    [pieData, lightColor, grayColor, secondaryColor, primaryColor],
  );

  const segments = useMemo(
    () => [
      { amount: pieData.expired.count, label: t('dashboard:charts.labels.expired'), value: formatCurrency(pieData.expired.totalFcExclVat) },
      {
        amount: pieData.approved.count,
        label: t('dashboard:charts.labels.notExpired'),
        value: formatCurrency(pieData.approved.totalFcExclVat),
      },
      {
        amount: pieData.paidCurrentQuarter.count,
        label: t('dashboard:charts.labels.currentQ', { quarter: dayjs().quarter() }),
        value: formatCurrency(pieData.paidCurrentQuarter.totalFcExclVat),
      },
      {
        amount: pieData.paidPreviousQuarters.count,
        label: t('dashboard:charts.labels.previousQ'),
        value: formatCurrency(pieData.paidPreviousQuarters.totalFcExclVat),
      },
    ],
    [
      formatCurrency,
      pieData.approved.count,
      pieData.approved.totalFcExclVat,
      pieData.expired.count,
      pieData.expired.totalFcExclVat,
      pieData.paidCurrentQuarter.count,
      pieData.paidCurrentQuarter.totalFcExclVat,
      pieData.paidPreviousQuarters.count,
      pieData.paidPreviousQuarters.totalFcExclVat,
      t,
    ],
  );

  const selectedData = useMemo(
    () =>
      selectedSegment !== null
        ? segments[selectedSegment]
        : { amount: pieData.total.count, label: t('dashboard:charts.summary.total'), value: formatCurrency(pieData.total.totalFcExclVat) },
    [selectedSegment, segments, pieData, t, formatCurrency],
  );

  const doughnutChartOptions = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      events: ['click'],
      animation: { duration: 500 },
      layout: {
        padding: 16,
      },
      plugins: {
        tooltip: { enabled: false },
        legend: {
          position: 'bottom' as const,
          align: 'center' as const,
          labels: {
            padding: 16,
            boxWidth: 24,
            boxHeight: 24,
            useBorderRadius: true,
            borderRadius: 4,
            font: { size: 14, family: fontFamily.secondary },
          },
          onClick: (_e: unknown, legendItem: { index?: number }) => {
            if (legendItem.index !== undefined) {
              const chart = chartRef.current;
              const index = legendItem.index;

              chart?.setActiveElements([{ datasetIndex: 0, index: index }]);
              setSelectedSegment(index);

              chart?.update();
            }
          },
        },
      },
      onClick: (_e, elements, chart) => {
        const selected = elements[0];
        if (!selected) {
          chart.setActiveElements([]);
          setSelectedSegment(null);
        } else {
          chart.setActiveElements([{ datasetIndex: 0, index: selected.index }]);
          setSelectedSegment(selected.index);
        }
      },
    }),
    [],
  );

  return (
    <>
      {/* Bar chart: total invoices */}
      <Card.Root>
        <Chart.Wrapper
          setYear={setTotalInvoicedYear}
          statistics={statistics}
          summary={!user.hasSubmittedInvoice && <ChartCta />}
          title={t('dashboard:charts.totalBilled')}
          year={totalInvoicedYear}
        >
          <Bar
            aria-label={t('dashboard:charts.totalBilledChart')}
            className={chartStyle}
            data={{ datasets: totalInvoicedDataset, labels: barChartLabels }}
            key={totalInvoicedYear}
            options={stackedBarOptions}
            plugins={[ctaAreaPlugin]}
          />
        </Chart.Wrapper>
      </Card.Root>

      {/* Pie chart: overview */}
      <Card.Root>
        <Chart.Wrapper
          setYear={setOverviewYear}
          statistics={statistics}
          summary={
            user.hasSubmittedInvoice ? (
              <Chart.Summary amount={selectedData.amount} label={selectedData.label} value={selectedData.value} />
            ) : (
              <ChartCta />
            )
          }
          title={t('dashboard:charts.overview')}
          year={overviewYear}
        >
          <Doughnut
            aria-label={t('dashboard:charts.overviewChart')}
            className={chartStyle}
            data={{ labels: overviewLabels, datasets: overviewDataset }}
            key={overviewYear}
            options={doughnutChartOptions}
            plugins={[ctaAreaPlugin]}
            ref={chartRef}
          />
        </Chart.Wrapper>
      </Card.Root>
    </>
  );
};
